import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import HasPermission from '../utils/HasPermission';
import { AuthContext } from '../context/AuthContext';
import './css/Users.css';
import { AdminTabs, DataTableShell, EmptyState, PageShell } from "../components/ui";

const apiUrl = API_URL;

const roleOptionsByActor = {
  superUser: ['user', 'verifiedUser', 'nationAdmin', 'supporter', 'superUser'],
  supporter: ['user', 'verifiedUser', 'nationAdmin'],
  nationAdmin: ['user', 'verifiedUser', 'nationAdmin']
};

const verificationRoleOptions = ['verifiedUser', 'nationAdmin'];

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const getNationId = (nation) => String(nation?._id || nation || '');

const Users = () => {
  const { user } = useContext(AuthContext);
  const canReadUsers = HasPermission('readUsers');
  const canUpdateUsers = HasPermission('updateUsers');
  const isSuperUser = user?.role === 'superUser';
  const [activeTab, setActiveTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [pendingRoleByUserId, setPendingRoleByUserId] = useState({});
  const [pendingNationByUserId, setPendingNationByUserId] = useState({});
  const [nations, setNations] = useState([]);
  const [savingUserId, setSavingUserId] = useState('');
  const [savingNationUserId, setSavingNationUserId] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingRequestRole, setPendingRequestRole] = useState({});
  const [actingRequestId, setActingRequestId] = useState('');
  const [isLoadingQueue, setIsLoadingQueue] = useState(false);

  const [auditItems, setAuditItems] = useState([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [expandedImageUrl, setExpandedImageUrl] = useState('');

  const assignableRoles = useMemo(
    () => roleOptionsByActor[user?.role] || [],
    [user?.role]
  );

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/users`);
      const fetchedUsers = response.data || [];
      setUsers(fetchedUsers);
      setPendingRoleByUserId(
        fetchedUsers.reduce((acc, currentUser) => {
          acc[currentUser._id] = currentUser.role;
          return acc;
        }, {})
      );
      setPendingNationByUserId(
        fetchedUsers.reduce((acc, currentUser) => {
          acc[currentUser._id] = getNationId(currentUser.nation);
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('Error fetching users:', error);
      window.alert('Failed to load users.');
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const fetchNations = useCallback(async () => {
    if (!isSuperUser) return;
    try {
      const response = await axiosInstance.get(`${apiUrl}/nations`);
      const fetchedNations = Array.isArray(response.data) ? response.data : [];
      const sortedNations = [...fetchedNations].sort((left, right) => {
        const leftNumber = Number(left?.number ?? 0);
        const rightNumber = Number(right?.number ?? 0);
        return leftNumber - rightNumber;
      });
      setNations(sortedNations);
    } catch (error) {
      console.error('Error fetching nations:', error);
      window.alert('Failed to load nations.');
    }
  }, [isSuperUser]);

  const fetchQueue = useCallback(async () => {
    setIsLoadingQueue(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/users/verification-requests`);
      const requests = response.data || [];
      setPendingRequests(requests);
      setPendingRequestRole(
        requests.reduce((acc, request) => {
          acc[request._id] = 'verifiedUser';
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('Error fetching verification queue:', error);
      window.alert('Failed to load verification queue.');
    } finally {
      setIsLoadingQueue(false);
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    if (user?.role !== 'superUser') return;
    setIsLoadingAudit(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/users/verification-audit`);
      setAuditItems(response.data || []);
    } catch (error) {
      console.error('Error fetching verification audit:', error);
      window.alert('Failed to load verification audit.');
    } finally {
      setIsLoadingAudit(false);
    }
  }, [user?.role]);

  useEffect(() => {
    if (!canReadUsers) return;
    if (activeTab === 'users') {
      fetchUsers();
      if (isSuperUser) fetchNations();
    }
    if (activeTab === 'queue') fetchQueue();
    if (activeTab === 'audit') fetchAudit();
  }, [activeTab, canReadUsers, fetchUsers, fetchQueue, fetchAudit, fetchNations, isSuperUser]);

  const handleSaveRole = async (targetUser) => {
    const nextRole = pendingRoleByUserId[targetUser._id];
    if (!nextRole || nextRole === targetUser.role) return;

    setSavingUserId(targetUser._id);
    try {
      await axiosInstance.put(`${apiUrl}/users/${targetUser._id}/role`, { role: nextRole });
      setUsers((previous) =>
        previous.map((currentUser) =>
          currentUser._id === targetUser._id
            ? { ...currentUser, role: nextRole }
            : currentUser
        )
      );
    } catch (error) {
      console.error('Error updating user role:', error);
      window.alert(typeof error === 'string' ? error : 'Failed to update role.');
      setPendingRoleByUserId((previous) => ({
        ...previous,
        [targetUser._id]: targetUser.role
      }));
    } finally {
      setSavingUserId('');
    }
  };

  const handleSaveNation = async (targetUser) => {
    if (!isSuperUser) return;
    const nextNationId = pendingNationByUserId[targetUser._id];
    const currentNationId = getNationId(targetUser.nation);
    if (!nextNationId || nextNationId === currentNationId) return;

    setSavingNationUserId(targetUser._id);
    try {
      const response = await axiosInstance.put(`${apiUrl}/users/${targetUser._id}/nation`, { nation: nextNationId });
      const updatedUser = response.data;
      setUsers((previous) =>
        previous.map((currentUser) =>
          currentUser._id === targetUser._id
            ? {
                ...currentUser,
                nation: updatedUser?.nation ?? currentUser.nation
              }
            : currentUser
        )
      );
    } catch (error) {
      console.error('Error updating user nation:', error);
      window.alert(typeof error === 'string' ? error : 'Failed to update nation.');
      setPendingNationByUserId((previous) => ({
        ...previous,
        [targetUser._id]: currentNationId
      }));
    } finally {
      setSavingNationUserId('');
    }
  };

  const handleApproveRequest = async (request) => {
    const role = pendingRequestRole[request._id] || 'verifiedUser';
    setActingRequestId(request._id);
    try {
      await axiosInstance.post(`${apiUrl}/users/verification-requests/${request._id}/action`, {
        action: 'approve',
        role
      });
      await fetchQueue();
      if (activeTab === 'users') {
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error approving verification request:', error);
      window.alert(typeof error === 'string' ? error : 'Failed to approve verification request.');
    } finally {
      setActingRequestId('');
    }
  };

  const handleCloseRequest = async (request) => {
    setActingRequestId(request._id);
    try {
      await axiosInstance.post(`${apiUrl}/users/verification-requests/${request._id}/action`, {
        action: 'close'
      });
      await fetchQueue();
    } catch (error) {
      console.error('Error closing verification request:', error);
      window.alert(typeof error === 'string' ? error : 'Failed to close verification request.');
    } finally {
      setActingRequestId('');
    }
  };

  if (!canReadUsers) {
    return (
      <PageShell maxWidth="xl">
        <EmptyState title="Unauthorized" description="You are not authorized to access user management." />
      </PageShell>
    );
  }

  const tabs = [
    { value: 'users', label: 'Users' },
    {
      value: 'queue',
      label: `Verification Queue${pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ''}`,
    },
    ...(user?.role === 'superUser' ? [{ value: 'audit', label: 'Verification Audit' }] : []),
  ];

  return (
    <PageShell maxWidth="xl">
      <div className="users-page">
        <AdminTabs tabs={tabs} value={activeTab} onChange={setActiveTab} className="users-tabs" />

      {activeTab === 'users' && (
        <DataTableShell title="Users" subtitle={`${users.length} total users`}>
          {isLoadingUsers ? (
            <div className="users-loading">Loading users...</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Nation</th>
                    <th>Role</th>
                    <th>Update Role</th>
                    {isSuperUser && <th>Update Nation</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map((targetUser) => {
                    const selectedRole = pendingRoleByUserId[targetUser._id] || targetUser.role;
                    const isSaving = savingUserId === targetUser._id;
                    const selectedNationId = pendingNationByUserId[targetUser._id] || getNationId(targetUser.nation);
                    const currentNationId = getNationId(targetUser.nation);
                    const nationSelectValue = nations.some((nationOption) => getNationId(nationOption) === selectedNationId)
                      ? selectedNationId
                      : '';
                    const isSavingNation = savingNationUserId === targetUser._id;
                    const isSelf = String(targetUser._id) === String(user?._id);
                    const canSave = canUpdateUsers && !isSelf && selectedRole !== targetUser.role;
                    const canSaveNation = canUpdateUsers && selectedNationId && selectedNationId !== currentNationId;

                    return (
                      <tr key={targetUser._id}>
                        <td>{targetUser.username}</td>
                        <td>{targetUser.nation?.number ?? 'N/A'}</td>
                        <td>{targetUser.role}</td>
                        <td className="users-actions-cell">
                          <select
                            value={selectedRole}
                            onChange={(event) =>
                              setPendingRoleByUserId((previous) => ({
                                ...previous,
                                [targetUser._id]: event.target.value
                              }))
                            }
                            disabled={!canUpdateUsers || isSaving || isSelf}
                          >
                            {assignableRoles.map((roleOption) => (
                              <option key={roleOption} value={roleOption}>
                                {roleOption}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveRole(targetUser)}
                            disabled={!canSave || isSaving}
                          >
                            {isSelf ? 'Locked' : isSaving ? 'Saving...' : 'Save'}
                          </button>
                        </td>
                        {isSuperUser && (
                          <td className="users-actions-cell">
                            <select
                              value={nationSelectValue}
                              onChange={(event) =>
                                setPendingNationByUserId((previous) => ({
                                  ...previous,
                                  [targetUser._id]: event.target.value
                                }))
                              }
                              disabled={isSavingNation}
                            >
                              {nations.length === 0 ? (
                                <option value="">No nations</option>
                              ) : (
                                nations.map((nationOption) => {
                                  const nationId = getNationId(nationOption);
                                  return (
                                    <option key={nationId} value={nationId}>
                                      {nationOption.number}
                                    </option>
                                  );
                                })
                              )}
                            </select>
                            <button
                              onClick={() => handleSaveNation(targetUser)}
                              disabled={!canSaveNation || isSavingNation}
                            >
                              {isSavingNation ? 'Saving...' : 'Save'}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DataTableShell>
      )}

      {activeTab === 'queue' && (
        <DataTableShell title="Verification Queue" subtitle={`${pendingRequests.length} pending requests`}>
          {isLoadingQueue ? (
            <div className="users-loading">Loading verification queue...</div>
          ) : pendingRequests.length === 0 ? (
            <div className="users-loading">No pending verification requests.</div>
          ) : (
            <div className="verification-queue-list">
              {pendingRequests.map((request) => (
                <div className="verification-queue-item" key={request._id}>
                  <div className="verification-queue-item-main">
                    <div className="verification-queue-meta">
                      <strong>{request.user?.username || 'Unknown User'}</strong>
                      <span>Nation: {request.nation?.number ?? 'N/A'}</span>
                      <span>Current Role: {request.user?.role || 'N/A'}</span>
                      <span>Submitted: {formatDateTime(request.createdAt)}</span>
                      {request.note && <span>Note: {request.note}</span>}
                    </div>
                    <div className="verification-queue-image-wrap">
                      <img
                        src={request.screenshotDataUrl}
                        alt="Commander verification screenshot"
                        onClick={() => setExpandedImageUrl(request.screenshotDataUrl)}
                        className="verification-clickable-image"
                      />
                    </div>
                  </div>
                  <div className="verification-queue-actions">
                    <select
                      value={pendingRequestRole[request._id] || 'verifiedUser'}
                      onChange={(event) =>
                        setPendingRequestRole((previous) => ({
                          ...previous,
                          [request._id]: event.target.value
                        }))
                      }
                      disabled={actingRequestId === request._id}
                    >
                      {verificationRoleOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleApproveRequest(request)}
                      disabled={actingRequestId === request._id}
                    >
                      {actingRequestId === request._id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      className="close-action"
                      onClick={() => handleCloseRequest(request)}
                      disabled={actingRequestId === request._id}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DataTableShell>
      )}

      {activeTab === 'audit' && user?.role === 'superUser' && (
        <DataTableShell title="Verification Audit" subtitle={`${auditItems.length} records`}>
          {isLoadingAudit ? (
            <div className="users-loading">Loading audit...</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Nation</th>
                    <th>Resolution</th>
                    <th>Resolved Role</th>
                    <th>Resolved By</th>
                    <th>Resolved At</th>
                  </tr>
                </thead>
                <tbody>
                  {auditItems.map((item) => (
                    <tr key={item._id}>
                      <td>{item.user?.username || 'N/A'}</td>
                      <td>{item.nation?.number ?? 'N/A'}</td>
                      <td>{item.resolution || 'N/A'}</td>
                      <td>{item.resolvedRole || 'N/A'}</td>
                      <td>{item.resolvedBy?.username || 'N/A'}</td>
                      <td>{formatDateTime(item.resolvedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataTableShell>
      )}

      {expandedImageUrl && (
        <div className="users-image-lightbox" onClick={() => setExpandedImageUrl('')}>
          <div className="users-image-lightbox-inner" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="users-image-lightbox-close"
              onClick={() => setExpandedImageUrl('')}
            >
              Close
            </button>
            <img src={expandedImageUrl} alt="Expanded verification screenshot" />
          </div>
        </div>
      )}
      </div>
    </PageShell>
  );
};

export default Users;
